import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Send, Paperclip, HelpCircle, Loader2, CheckCircle2, ChevronDown } from "lucide-react";
import api from "../lib/api";

export default function SupportDrawer({ open, onClose }) {
  const [tab, setTab] = useState("faq");
  const [faqs, setFaqs] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotName, setScreenshotName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/support/faqs").then((r) => setFaqs(r.data.faqs || [])).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open) {
      setSubmitted(false);
      setSubject("");
      setMessage("");
      setScreenshot(null);
      setScreenshotName("");
    }
  }, [open]);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) {
      toast.error("Screenshot must be under 2 MB");
      return;
    }
    const r = new FileReader();
    r.onload = (ev) => {
      setScreenshot(ev.target.result);
      setScreenshotName(f.name);
    };
    r.readAsDataURL(f);
  };

  const submit = async () => {
    if (!subject.trim() || !message.trim()) {
      toast.error("Please add a subject and describe the issue");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/support/tickets", {
        subject: subject.trim(),
        message: message.trim(),
        screenshot_base64: screenshot,
      });
      setSubmitted(true);
      toast.success("Ticket sent — we'll get back to you soon");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send ticket");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center" data-testid="support-drawer">
      <div
        className="absolute inset-0 bg-black/45 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <div className="relative w-full max-w-md bg-white rounded-t-3xl max-h-[86vh] overflow-hidden flex flex-col shadow-2xl">
        {/* Handle */}
        <div className="pt-2 pb-1 flex justify-center">
          <span className="w-10 h-1 bg-neutral-200 rounded-full" />
        </div>
        <div className="flex items-center justify-between px-5 pt-2 pb-3 border-b border-neutral-100">
          <div>
            <div className="font-display font-bold text-[18px] tracking-tight">Support</div>
            <div className="text-[11px] text-neutral-500">We usually reply within 24 hours</div>
          </div>
          <button
            onClick={onClose}
            data-testid="support-close-btn"
            className="w-8 h-8 rounded-full hover:bg-neutral-100 flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-5 pt-3 flex gap-2">
          <button
            onClick={() => setTab("faq")}
            data-testid="support-tab-faq"
            className={`h-9 px-4 rounded-full text-[12.5px] font-semibold flex items-center gap-1.5 ${
              tab === "faq" ? "bg-black text-white" : "bg-neutral-100 text-neutral-600"
            }`}
          >
            <HelpCircle className="w-3.5 h-3.5" /> FAQs
          </button>
          <button
            onClick={() => setTab("ticket")}
            data-testid="support-tab-ticket"
            className={`h-9 px-4 rounded-full text-[12.5px] font-semibold flex items-center gap-1.5 ${
              tab === "ticket" ? "bg-black text-white" : "bg-neutral-100 text-neutral-600"
            }`}
          >
            <Send className="w-3.5 h-3.5" /> Raise ticket
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {tab === "faq" && (
            <div className="space-y-2" data-testid="support-faq-list">
              {faqs.map((f, i) => (
                <div
                  key={i}
                  className="rounded-2xl border border-neutral-200 bg-white overflow-hidden"
                  data-testid={`faq-item-${i}`}
                >
                  <button
                    onClick={() => setExpanded(expanded === i ? null : i)}
                    className="w-full flex items-center justify-between text-left px-4 py-3"
                  >
                    <span className="text-[13.5px] font-semibold pr-3">{f.q}</span>
                    <ChevronDown
                      className={`w-4 h-4 flex-shrink-0 transition-transform ${
                        expanded === i ? "rotate-180 text-brand" : "text-neutral-400"
                      }`}
                    />
                  </button>
                  {expanded === i && (
                    <div className="px-4 pb-3 text-[12.5px] text-neutral-600 leading-relaxed">
                      {f.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {tab === "ticket" && !submitted && (
            <div className="space-y-3">
              <div>
                <label className="text-[12px] font-semibold text-neutral-700 block mb-1.5">Subject</label>
                <input
                  data-testid="ticket-subject-input"
                  className="input-native"
                  placeholder="e.g. Payment failed but amount deducted"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  maxLength={200}
                />
              </div>
              <div>
                <label className="text-[12px] font-semibold text-neutral-700 block mb-1.5">Message</label>
                <textarea
                  data-testid="ticket-message-input"
                  className="input-native !h-auto min-h-[110px] py-3"
                  placeholder="Describe your issue in detail…"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  maxLength={4000}
                />
              </div>
              <div>
                <label className="text-[12px] font-semibold text-neutral-700 block mb-1.5">Screenshot (optional)</label>
                <label className="input-native flex items-center justify-between cursor-pointer text-neutral-500">
                  <span className="flex items-center gap-2 text-[13px]">
                    <Paperclip className="w-4 h-4" />
                    {screenshotName || "Attach a screenshot"}
                  </span>
                  <span className="text-[11px] text-brand font-semibold">Browse</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={onPickFile}
                    data-testid="ticket-screenshot-input"
                  />
                </label>
                {screenshot && (
                  <img
                    src={screenshot}
                    alt="preview"
                    className="mt-2 max-h-32 rounded-lg border border-neutral-200"
                  />
                )}
              </div>

              <div className="text-[11px] text-neutral-500">
                Your phone number and email are automatically attached with the ticket.
              </div>

              <button
                onClick={submit}
                disabled={submitting}
                data-testid="ticket-submit-btn"
                className="btn-brand w-full h-11 flex items-center justify-center gap-2 text-[14px] mt-2"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send ticket"}
              </button>
            </div>
          )}

          {tab === "ticket" && submitted && (
            <div className="text-center py-10" data-testid="ticket-submitted-state">
              <div className="w-14 h-14 rounded-full bg-emerald-50 mx-auto flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7 text-emerald-500" />
              </div>
              <div className="mt-3 font-semibold text-[16px]">Ticket received</div>
              <div className="mt-1 text-[12.5px] text-neutral-500">We'll reply within 24 hours on your registered phone/email.</div>
              <button
                onClick={onClose}
                className="mt-6 h-10 px-6 rounded-full border border-neutral-200 text-[13px] font-semibold hover:bg-neutral-50"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
